import type { ConfigType } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/en'
import 'dayjs/locale/en-au'
import 'dayjs/locale/en-ca'
import 'dayjs/locale/en-gb'
import 'dayjs/locale/en-ie'
import 'dayjs/locale/fr'
import 'dayjs/locale/fr-ca'
import 'dayjs/locale/fr-ch'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import type { DateTimeFormatOptions } from 'next-intl'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import emDash from '~/components/emdash'
import useLogger from '~/debug/logger'
import useDateTimeLocale from '~/hooks/useDateTimeLocale'
import { useUserPrefs } from '~/hooks/useUserPrefs'

import type { TimeRange } from '../time/types'
import type { TimeZone } from '../types/timezone'

dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(minMax)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(utc)

function validateTimeZone(timeZone?: string) {
  try {
    dayjs().tz(timeZone)
    return timeZone
  } catch (error) {
    return null
  }
}

const SINGLE_FORMATS = ['time', 'date', 'dateMedium'] as const
type SingleFormat = (typeof SINGLE_FORMATS)[number]
type CommonFormat = 'time' | 'date' | 'dateDotTime' | 'timeDotDate' | 'timeOnDate' | 'dateMedium' | 'export'

const formatOptions: Record<SingleFormat, DateTimeFormatOptions> = {
  time: { timeStyle: 'short' },
  date: { dateStyle: 'short' },
  dateMedium: { day: 'numeric', month: 'short', year: 'numeric' }
}

function isSingleFormatKey(formatOrOptions: CommonFormat | DateTimeFormatOptions): formatOrOptions is SingleFormat {
  return SINGLE_FORMATS.includes(formatOrOptions as SingleFormat)
}

export type ExportStyle = 'date' | 'datetime' | 'full'
type InvalidStr = { invalidStr?: string }
type Options = InvalidStr & {
  includeSeconds?: boolean
  exportStyle?: ExportStyle
  timeZone?: string
}

/**
 * Hook that provides a flexible date/time formatting function using next-intl.
 *
 * @returns A callback function that formats date/time values with various options
 *
 * @example
 * ```tsx
 * const format = useFormatters();
 *
 * // Format with predefined formats
 * format.dateTime(new Date(), 'time'); // "3:45 PM"
 * format.dateTime(new Date(), 'date'); // "12/25/23"
 * format.dateTime(new Date(), 'dateDotTime'); // "12/25/23 • 3:45 PM"
 * format.dateTime(new Date(), 'timeOnDate'); // "3:45 PM on 12/25/23"
 * format.dateTime(new Date(), 'timeDotDate'); // "3:45 PM • 12/25/23"
 *
 * // Format with custom options
 * format.dateTime(new Date(), { dateStyle: 'full', timeStyle: 'medium' });
 *
 * // Format with overrides
 * format.dateTime(new Date(), 'time', {
 *   includeSeconds: true, // Shows seconds
 *   invalidStr: 'N/A' // Custom invalid date string
 * });
 *
 * // Export formats
 * format.dateTime(new Date(), 'export', { exportStyle: 'datetime' }); // "2023-12-25 15:45:30"
 * ```
 *
 * The callback accepts:
 * - `value`: Any date/time value that dayjs can parse (Date, string, number, etc.)
 * - `formatOrOptions`: Either a predefined format string or custom DateTimeFormatOptions
 * - `options`: Additional options for overrides, timezone, and invalid string handling
 *
 * Predefined formats:
 * - `'time'`: Short time format (e.g., "3:45 PM")
 * - `'date'`: Short date format (e.g., "12/25/23")
 * - `'dateMedium'`: Medium date format (e.g., "Dec 25, 2023")
 * - `'dateDotTime'`: Date and time separated by bullet (e.g., "12/25/23 • 3:45 PM")
 * - `'timeDotDate'`: Same as dateDotTime but with time first (e.g., "3:45 PM • 12/25/23")
 * - `'timeOnDate'`: Time with "on" date (e.g., "3:45 PM on 12/25/23")
 * - `'export'`: Export format using dayjs formatting (requires exportStyle option)
 *
 * Options:
 * - `invalidStr`: String to return for invalid dates (default: em dash)
 * - `includeSeconds`: Whether to include seconds in the time format
 * - `exportStyle`: Style for export format ('date', 'datetime', or 'full')
 * - `timeZone`: Override the user's preferred timezone
 */
export function useDateTimeFormatter() {
  const t = useTranslations('data.time')
  const logger = useLogger()
  const { preferredTimeZone } = useUserPrefs()
  const dtLocale = useDateTimeLocale()

  return useCallback(
    (
      value: ConfigType,
      formatOrOptions: CommonFormat | (DateTimeFormatOptions & InvalidStr),
      options?: Options
    ): string => {
      const { includeSeconds, exportStyle, timeZone: tzOption } = options ?? {}
      const invalidStr = getInvalidStr(formatOrOptions, options)
      if (!value) return invalidStr

      const timeZone = validateTimeZone(tzOption) ?? preferredTimeZone
      const d = dayjs(value).tz(timeZone)
      const val = d.isValid() ? d.toDate() : null
      if (!val) return invalidStr

      if (typeof formatOrOptions !== 'string') {
        // Raw format options. This is triggered when the format is specified as an object, e.g.
        // format.dateTime(dt, { dateStyle: 'full', timeStyle: 'medium' })
        return Intl.DateTimeFormat(dtLocale, { timeZone, ...formatOrOptions }).format(val)
      }

      const timeOverrides: DateTimeFormatOptions = includeSeconds ? { timeStyle: 'medium' } : {}
      if (isSingleFormatKey(formatOrOptions)) {
        // Common format options. This is triggered when the format is specified as a string that
        // contains either a date or time, but not both, e.g. format.dateTime(dt, 'time')
        const dtf = new Intl.DateTimeFormat(dtLocale, { timeZone, ...formatOptions[formatOrOptions], ...timeOverrides })
        return dtf.format(val)
      } else if (formatOrOptions === 'export') {
        if (!exportStyle) {
          logger.error('exportStyle is required when formatOrOptions is "export"')
        }
        return d.format(getExportFormat(exportStyle ?? 'date'))
      }

      // The specified format is a combination of date and time, e.g. format.dateTime(dt, 'dateDotTime')
      // Format each individually and combine with localized text
      const time = Intl.DateTimeFormat(dtLocale, { timeZone, ...formatOptions.time, ...timeOverrides }).format(val)
      const date = Intl.DateTimeFormat(dtLocale, { timeZone, ...formatOptions.date }).format(val)

      switch (formatOrOptions) {
        case 'timeOnDate':
          return t('datetime_timeOnDate', { time, date })
        case 'timeDotDate':
          return `${time} • ${date}`
        case 'dateDotTime':
          return `${date} • ${time}`
      }
    },
    [logger, preferredTimeZone, t, dtLocale]
  )
}

type DateTimeRangeFormat = 'times' | 'dates' | 'dateTimes'
type DateTimeRangeOptions = {
  timeZone?: TimeZone
}

/**
 * A React hook that provides a date/time range formatting function.
 *
 * @returns A callback function that formats date/time ranges in various styles
 *
 * @example
 * ```tsx
 * const format = useFormatters();
 * const range = { from: dayjs('2023-12-25 09:00'), to: dayjs('2023-12-25 17:00') };
 *
 * // Format times only
 * format.dateTimeRange(range, 'times'); // "9:00 AM - 5:00 PM"
 *
 * // Format dates only (smart formatting for same day)
 * format.dateTimeRange(range, 'dates'); // "12/25/23" (same day)
 * format.dateTimeRange(differentDayRange, 'dates'); // "12/25/23 - 12/26/23"
 *
 * // Format full date/time range (localized)
 * format.dateTimeRange(range, 'dateTimes'); // "9:00 AM on 12/25/23 to 5:00 PM"
 * format.dateTimeRange(differentDayRange, 'dateTimes'); // "9:00 AM on 12/25/23 to 5:00 PM on 12/26/23"
 *
 * // Override timezone
 * format.dateTimeRange(range, 'times', { timeZone: 'America/New_York' }); // "12:00 PM - 8:00 PM"
 * ```
 *
 * The callback accepts:
 * - `range`: TimeRange object with `from` and `to` dayjs instances
 * - `formatOrOptions`: Format style for the range
 * - `options`: Optional settings including timezone override
 *
 * Format options:
 * - `'times'`: Shows only the time range (e.g., "9:00 AM - 5:00 PM")
 * - `'dates'`: Shows only the date range, with smart formatting for same day
 * - `'dateTimes'`: Shows full date/time range with localized connecting text
 *
 * Special behaviors:
 * - For 'dates' format: If both dates are the same day, only shows one date
 * - For 'dateTimes' format: Uses localized text that adapts based on whether
 *   the range spans multiple days
 * - All formats respect the user's preferred timezone unless overridden
 */
export function useDateTimeRangeFormatter() {
  const t = useTranslations('data.time')
  const formatDateTime = useDateTimeFormatter()
  const { preferredTimeZone } = useUserPrefs()

  return useCallback(
    (range: TimeRange, formatOrOptions: DateTimeRangeFormat, options?: DateTimeRangeOptions): string => {
      const timeZone = validateTimeZone(options?.timeZone) ?? preferredTimeZone
      const from = dayjs(range.from).tz(timeZone)
      const to = dayjs(range.to).tz(timeZone)
      const dtOptions = { timeZone }

      switch (formatOrOptions) {
        case 'times':
          return `${formatDateTime(from, 'time', dtOptions)} - ${formatDateTime(to, 'time', dtOptions)}`
        case 'dates':
          if (from.isSame(to, 'day')) {
            return formatDateTime(from, 'date', dtOptions)
          } else {
            return `${formatDateTime(from, 'date', dtOptions)} - ${formatDateTime(to, 'date', dtOptions)}`
          }
        case 'dateTimes':
          return t('datetime_dateTimes', {
            fromTime: formatDateTime(from, 'time', dtOptions),
            fromDate: formatDateTime(from, 'date', dtOptions),
            toTime: formatDateTime(to, 'time', dtOptions),
            toDate: formatDateTime(to, 'date', dtOptions),
            sameDay: String(from.isSame(to, 'day'))
          })
      }
    },
    [formatDateTime, preferredTimeZone, t]
  )
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/**
 * Hook that returns a function rendering a locale-aware relative-time
 * phrase from a target time and a "now" reference.
 *
 *   format.relative(eventTime, now)
 *     // → "just now" / "5 minutes ago" / "yesterday" / "in 3 days"
 *
 * Behaviour:
 * - Anything less than 60 seconds from `now` (in either direction) reads
 *   as "just now" — covers small server/client clock skew that would
 *   otherwise render fresh events as "in 30 seconds".
 * - Beyond ±60s, uses `Intl.RelativeTimeFormat` with `numeric: 'auto'` so
 *   the phrase reads idiomatically ("yesterday" rather than "1 day ago").
 *   The largest unit that fits the magnitude is chosen (seconds → minutes
 *   → hours → days).
 *
 * Output language follows the user's preference via {@link useDateTimeLocale},
 * matching the other date formatters in this module.
 */
export function useDateTimeRelativeFormatter() {
  const t = useTranslations('data.time')
  const dtLocale = useDateTimeLocale()
  const rtf = useMemo(() => new Intl.RelativeTimeFormat(dtLocale, { numeric: 'auto' }), [dtLocale])

  return useCallback(
    (target: ConfigType, now: ConfigType): string => {
      const diffMs = dayjs(target).diff(dayjs(now))
      const absMs = Math.abs(diffMs)
      if (absMs < MINUTE_MS) return t('relative_justNow')
      if (absMs < HOUR_MS) return rtf.format(Math.round(diffMs / MINUTE_MS), 'minute')
      if (absMs < DAY_MS) return rtf.format(Math.round(diffMs / HOUR_MS), 'hour')
      return rtf.format(Math.round(diffMs / DAY_MS), 'day')
    },
    [t, rtf]
  )
}

/**
 * Calendar-day distance beyond which a day reads as an absolute date. One day
 * is what `Intl.RelativeTimeFormat`'s `numeric: 'auto'` renders as a real word
 * in every locale ("yesterday" / "today" / "tomorrow"); at two days it falls
 * back to "2 days ago", which is harder to scan than the date itself.
 */
const RELATIVE_DAY_LIMIT = 1

/**
 * Hook that returns a function labelling the calendar day a timestamp falls on,
 * for grouping headings in day-bucketed lists.
 *
 *   format.dateTimeRelativeDay(note.timestamp, today)
 *     // → "yesterday" / "today" / "Jul 24, 2026"
 *
 * Both arguments are reduced to the start of their calendar day in the user's
 * preferred timezone before comparison, so a timestamp late on one evening and
 * one early the next morning land in different buckets even when they are only
 * minutes apart. Pass `today` from {@link useTzToday} so the label re-renders
 * when the day rolls over.
 *
 * Within a day of the reference, the label is a locale-aware relative word;
 * beyond that it is the `dateMedium` date. Relative words come out lowercased
 * in English ("today") — style the heading with `textTransform` rather than
 * capitalizing the string, which would be wrong in several locales.
 */
export function useDateTimeRelativeDayFormatter() {
  const formatDateTime = useDateTimeFormatter()
  const dtLocale = useDateTimeLocale()
  const { tzDayjs } = useUserPrefs()
  const rtf = useMemo(() => new Intl.RelativeTimeFormat(dtLocale, { numeric: 'auto' }), [dtLocale])

  return useCallback(
    (value: ConfigType, today: ConfigType): string => {
      const day = tzDayjs(value).startOf('day')
      if (!day.isValid()) return emDash

      const diffDays = day.diff(tzDayjs(today).startOf('day'), 'day')
      if (Math.abs(diffDays) <= RELATIVE_DAY_LIMIT) return rtf.format(diffDays, 'day')
      return formatDateTime(day, 'dateMedium')
    },
    [formatDateTime, rtf, tzDayjs]
  )
}

function getExportFormat(exportStyle: ExportStyle) {
  switch (exportStyle) {
    case 'date':
      return 'YYYY-MM-DD'
    case 'datetime':
      return 'YYYY-MM-DD HH:mm:ss'
    case 'full':
      return 'YYYY-MM-DD HH:mm:ss.SSS'
  }
}

/**
 * Returns the appropriate string to display for invalid values. For export formats, returns empty
 * string; for display formats, returns em dash (—). Can be overridden via options.invalidStr.
 */
function getInvalidStr(formatOrOptions: CommonFormat | InvalidStr, options?: Options) {
  if (typeof formatOrOptions === 'string') {
    return options?.invalidStr ?? (formatOrOptions === 'export' ? '' : emDash)
  } else {
    return formatOrOptions.invalidStr ?? emDash
  }
}
